import type { RequestParams } from '@robovision/rvai-auth'
import { HttpStatusCode } from '@robovision/rvai-utils'
import mitt from 'mitt'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import type { LocationQueryRaw } from 'vue-router'
import { License } from '~domain/License'
import type { LoginData } from '~domain/LoginData'
import { Member } from '~domain/Member'
import {
  dataApi,
  hubApi,
  rvaiAuthApi,
} from '~services/api'
import { queryClient } from '~services/query-client'
import {
  acceptStatus,
  retryIfUnauthorized,
} from '~utils/api'
import {
  ENTITLEMENTS_QUERY_KEY,
  getEntitlementsQueryOptions,
} from '~utils/api/getEntitlements'
import { useLicensing } from './composables/useLicensing'
import { useLicenseStore } from './useLicenseStore'


interface LoginPayload {
  user: Member
}

interface LogoutPayload {
  query?: LocationQueryRaw
  redirectToLogin?: boolean
}

export const EventLogin = Symbol('Login')
export const EventLogout = Symbol('Logout')

export const emitter = mitt<{
  [EventLogin]: LoginPayload
  [EventLogout]: LogoutPayload
}>()

async function fetchLicense (params?: RequestParams): Promise<License | undefined> {
  try {
    const { data } = await queryClient.fetchQuery({
      queryFn: () => rvaiAuthApi.license.getLicense(params),
      queryKey: ['rvaiAuthApi', 'license'],
      // Fail gracefully and don't retry, to avoid stalling on startup
      retry: false,
    })

    return License.fromResponse(data)
  } catch {
    console.warn('Failed to fetch license')

    return undefined
  }
}

async function fetchEntitlements (): Promise<void> {
  try {
    await queryClient.fetchQuery({
      ...getEntitlementsQueryOptions(),
      // Fail gracefully and don't retry, to avoid stalling on startup
      retry: false,
    })
  } catch {
    console.warn('Failed to fetch entitlements')
  }
}

export const useSessionStore = defineStore('session', () => {
  const { setLicense } = useLicenseStore()
  const { setLicenseNotificationShown } = useLicensing()

  const state = reactive({
    currentUser: undefined as Member | undefined,
  })

  const getters = {
    getCurrentUser (): Member | undefined {
      return state.currentUser
    },
  }

  const actions = {
    async checkHealth () {
      try {
        await Promise.all([
          hubApi.livez.getHeartbeat(),
          dataApi.livez.isLive(),
        ])
      } catch (error) {
        this.resetSession()
        throw error
      }
    },

    async loadSession () {
      try {
        const { data: userData } = await rvaiAuthApi.users.getCurrentUser()
        const [license] = await Promise.all([
          fetchLicense({
            validateStatus: acceptStatus(HttpStatusCode.UNPROCESSABLE_ENTITY),
          }),
          // useQuery in useEntitlementsQuery() is the source of truth, so
          // there's no need to store the result here.
          fetchEntitlements(),
        ])
        this.setSession(Member.fromResponse(userData), license)
      } catch (error) {
        this.resetSession()
        throw error
      }
    },

    async login ({
      password,
      username,
    }: LoginData) {
      await rvaiAuthApi.login.login({
        password,
        username,
      })

      // this retry mechanism is necessary to handle the scenario
      // where the auth service returns a valid response for the login
      // but the jwt token has not been stored into the DB yet,
      // which causes the subsequent calls to fail for a brief period.
      // TODO remove the retry once the issue is fixed in RVAI-9445
      const currentUserData = await retryIfUnauthorized(
        async () => {
          const { data } = await rvaiAuthApi.users.getCurrentUser()
          return data
        },
      )

      const [license] = await Promise.all([
        fetchLicense(),
        // useQuery in useEntitlementsQuery() is the source of truth, so
        // there's no need to store the result here.
        fetchEntitlements(),
      ])
      this.setSession(Member.fromResponse(currentUserData), license)
    },

    async logout (params?: {
      query?: LocationQueryRaw
      redirectToLogin?: boolean
    }) {
      await rvaiAuthApi.logout.logout({
        validateStatus: acceptStatus(HttpStatusCode.UNAUTHORIZED),
      })

      this.resetSession(params)
    },

    onLogin (handler: (payload: LoginPayload) => void): void {
      emitter.on(EventLogin, handler)
    },

    onLogout (handler: (payload: LogoutPayload) => void): void {
      emitter.on(EventLogout, handler)
    },

    resetSession (params: {
      query?: LocationQueryRaw
      redirectToLogin?: boolean
    } = { redirectToLogin: true }) {
      state.currentUser = undefined
      setLicenseNotificationShown(false)
      queryClient.removeQueries({ queryKey: ENTITLEMENTS_QUERY_KEY })

      emitter.emit(EventLogout, params)
    },

    setSession (user: Member, license?: License) {
      state.currentUser = user

      setLicense(license)

      emitter.emit(EventLogin, {
        user: state.currentUser,
      })
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...actions,
    ...getters,
  }
})
